import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, X, Check, Sparkles } from 'lucide-react';
import { searchAddressSuggestions, AddressSuggestion } from '../../lib/addressAutocompleteService';
import { resolveCountryPhoneInfo } from '../../lib/countryPhoneData';

interface AddressAutocompleteInputProps {
  value: string;
  onChange: (fullAddress: string) => void;
  selectedCountry: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

export const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  value,
  onChange,
  selectedCountry,
  placeholder = 'e.g. Street address, apartment, city, state',
  required = true,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Sync external value
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Handle typing & debounced live address search
  useEffect(() => {
    const trimmed = inputValue.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchAddressSuggestions(trimmed, selectedCountry, controller.signal);
        setSuggestions(results);
        if (results.length > 0) {
          setIsOpen(true);
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.warn('Address search error:', err);
        }
      } finally {
        setIsLoading(false);
      }
    }, 280);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [inputValue, selectedCountry]);

  // Click outside to dismiss dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
    setActiveIndex(-1);
  };

  const handleSelectSuggestion = (suggestion: AddressSuggestion) => {
    setInputValue(suggestion.fullAddress);
    onChange(suggestion.fullAddress);
    setSuggestions([]);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
    setSuggestions([]);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        handleSelectSuggestion(suggestions[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const countryInfo = resolveCountryPhoneInfo(selectedCountry);

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative flex items-center">
        {/* Left Map Pin Icon */}
        <div className="absolute left-3.5 pointer-events-none text-slate-400">
          <MapPin className="w-4 h-4 text-cyan-400" />
        </div>

        {/* Text Input */}
        <input
          type="text"
          value={inputValue}
          disabled={disabled}
          onChange={handleInputChange}
          onFocus={() => {
            if (suggestions.length > 0) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-xs font-bold text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-400 transition-colors"
          required={required}
          autoComplete="off"
        />

        {/* Right Status / Action */}
        <div className="absolute right-3 flex items-center gap-1.5">
          {isLoading && (
            <Loader2 className="w-3.5 h-3.5 text-cyan-400 animate-spin" />
          )}
          {!isLoading && inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
              title="Clear Address"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Live Address Suggestions Dropdown (NO Google Map, pure clean list) */}
      {isOpen && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1.5 bg-slate-900/98 backdrop-blur-xl border border-cyan-500/40 rounded-2xl shadow-2xl z-[110] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
          {/* Header Bar */}
          <div className="px-3 py-2 bg-slate-950/80 border-b border-white/10 flex items-center justify-between text-[11px] font-bold text-slate-400">
            <span className="flex items-center gap-1.5 text-cyan-300">
              <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
              <span>Recommended Addresses ({countryInfo.name})</span>
            </span>
            <span className="text-[10px] text-slate-500">Click to select</span>
          </div>

          {/* Suggestions List */}
          <div className="max-h-64 overflow-y-auto p-1.5 space-y-1 divide-y divide-white/5 scrollbar-thin scrollbar-thumb-slate-700">
            {suggestions.map((item, idx) => {
              const isActive = activeIndex === idx;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleSelectSuggestion(item)}
                  onMouseEnter={() => setActiveIndex(idx)}
                  className={`w-full text-left p-2.5 rounded-xl transition-all flex items-start gap-2.5 cursor-pointer ${
                    isActive
                      ? 'bg-cyan-500/20 border border-cyan-400/40 text-white'
                      : 'text-slate-200 hover:bg-slate-800/80'
                  }`}
                >
                  <div className="p-1.5 rounded-lg bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 shrink-0 mt-0.5">
                    <MapPin className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-white truncate">{item.label}</p>
                    {item.secondary && (
                      <p className="text-[11px] text-slate-400 truncate mt-0.5">{item.secondary}</p>
                    )}
                  </div>
                  {isActive && (
                    <div className="text-cyan-400 shrink-0 mt-1">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
